// ─────────────────────────────────────────────────────────────────────────
// useSignatureCanvas.ts
// Ports resizeCanvas(), getPos(), startPosition/draw/endPosition, and the
// window 'resize' handler that redraws via a temporary canvas buffer so a
// signature already drawn isn't lost when the layout/orientation changes.
//
// Usage:
//   const sig = useSignatureCanvas();
//   <div ref={sig.containerRef}>
//     <canvas ref={sig.canvasRef} {...sig.canvasHandlers} />
//   </div>
//   sig.hasSignature, sig.clear(), sig.toDataURL(), sig.prepareForDisplay()
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSignatureCanvasResult {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  hasSignature: boolean;
  /** Call once the canvas's container becomes visible/sized (e.g. on modal open) to size + style the context. */
  prepareForDisplay: () => void;
  clear: () => void;
  toDataURL: () => string;
  canvasHandlers: {
    onMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseUp: () => void;
    onMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseLeave: () => void;
    onTouchStart: (e: React.TouchEvent<HTMLCanvasElement>) => void;
    onTouchEnd: () => void;
    onTouchMove: (e: React.TouchEvent<HTMLCanvasElement>) => void;
  };
}

function getPointerPos(
  canvas: HTMLCanvasElement,
  e: { clientX: number; clientY: number },
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

export function useSignatureCanvas(): UseSignatureCanvasResult {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(false);

  const styleContext = useCallback((ctx: CanvasRenderingContext2D) => {
    ctx.strokeStyle = '#1A0033';
    ctx.lineWidth = 5.0;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  /** Equivalent to resizeCanvas(): sizes the backing buffer to the container's CSS box * DPR. */
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentElement) return;
    const rect = canvas.parentElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    styleContext(ctx);
  }, [styleContext]);

  const prepareForDisplay = useCallback(() => {
    // Mirrors the original's setTimeout(... resizeCanvas(), 10) inside openSignatureModal,
    // giving the modal's transition/layout a tick to settle before measuring.
    window.setTimeout(resizeCanvas, 10);
  }, [resizeCanvas]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    ctx.beginPath();
    setHasSignature(false);
  }, []);

  const toDataURL = useCallback((): string => {
    return canvasRef.current?.toDataURL('image/png') ?? '';
  }, []);

  const drawTo = useCallback((x: number, y: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPosRef.current = { x, y };
  }, []);

  const startAt = useCallback((x: number, y: number) => {
    isDrawingRef.current = true;
    setHasSignature(true);
    lastPosRef.current = { x, y };
    drawTo(x, y);
  }, [drawTo]);

  const endStroke = useCallback(() => {
    isDrawingRef.current = false;
    canvasRef.current?.getContext('2d')?.beginPath();
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current) return;
      const p = getPointerPos(canvasRef.current, e);
      startAt(p.x, p.y);
    },
    [startAt],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || !canvasRef.current) return;
      const p = getPointerPos(canvasRef.current, e);
      drawTo(p.x, p.y);
    },
    [drawTo],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current || e.touches.length === 0) return;
      const touch = e.touches[0];
      const p = getPointerPos(canvasRef.current, touch);
      startAt(p.x, p.y);
    },
    [startAt],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || !canvasRef.current || e.touches.length === 0) return;
      // preventDefault stops the page from scrolling while signing, same as the original.
      e.preventDefault();
      const touch = e.touches[0];
      const p = getPointerPos(canvasRef.current, touch);
      drawTo(p.x, p.y);
    },
    [drawTo],
  );

  // ── Orientation/resize handling with temp-buffer redraw, preserving an in-progress signature ──
  useEffect(() => {
    function handleWindowResize() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Only bother preserving + resizing if the canvas is actually laid out (e.g. modal open).
      const isVisible = canvas.offsetParent !== null;
      if (!isVisible) return;

      const tmp = document.createElement('canvas');
      const tmpCtx = tmp.getContext('2d');
      if (!tmpCtx) return;
      tmp.width = canvas.width;
      tmp.height = canvas.height;
      tmpCtx.drawImage(canvas, 0, 0);

      resizeCanvas();

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(tmp, 0, 0, tmp.width, tmp.height, 0, 0, canvas.width, canvas.height);
    }

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [resizeCanvas]);

  return {
    canvasRef,
    hasSignature,
    prepareForDisplay,
    clear,
    toDataURL,
    canvasHandlers: {
      onMouseDown,
      onMouseUp: endStroke,
      onMouseMove,
      onMouseLeave: endStroke,
      onTouchStart,
      onTouchEnd: endStroke,
      onTouchMove,
    },
  };
}
